/**
 * useLoadMore Hook
 *
 * Reusable hook for offset-based "load more" pagination.
 * Works with any async fetcher that accepts { offset, limit } and
 * returns { data: T[]; count: number }.
 */

import { useState, useCallback, useRef, useEffect } from "react";

export type LoadMoreFetcher<T> = (params: { offset: number; limit: number }) => Promise<{
  data: T[];
  count: number;
}>

export interface UseLoadMoreOptions<T> {
  /** Async function that fetches a page of data. */
  fetcher: LoadMoreFetcher<T>;
  /** Number of items per page (default 50). */
  pageSize?: number;
  /**
   * When this value changes the list is reset and re-fetched from scratch.
   * Typically a job ID, filter key, etc.
   */
  resetKey?: string;
  /** Whether the hook should fetch automatically on mount / reset (default true). */
  enabled?: boolean;
}

export interface UseLoadMoreResult<T> {
  /** All items accumulated so far. */
  items: T[];
  /** Total count reported by the server. */
  totalCount: number;
  /** True during the initial load (no items yet). */
  isLoading: boolean;
  /** True while loading an additional page. */
  isLoadingMore: boolean;
  /** Whether there are more items to load. */
  hasMore: boolean;
  /** Load the next page and append to the list. */
  loadMore: () => Promise<void>;
  /** Reset and reload from scratch. */
  reset: () => Promise<void>;
}

const DEFAULT_PAGE_SIZE = 50;

export function useLoadMore<T>(
  options: UseLoadMoreOptions<T>,
): UseLoadMoreResult<T> {
  const {
    fetcher,
    pageSize = DEFAULT_PAGE_SIZE,
    resetKey,
    enabled = true,
  } = options;

  const [items, setItems] = useState<T[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Keep refs so callers don't need to memoize.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const pageSizeRef = useRef(pageSize);
  pageSizeRef.current = pageSize;
  const resetKeyRef = useRef(resetKey);

  // Track current item count for offset calculation in loadMore.
  const itemCountRef = useRef(0);

  const loadInitial = useCallback(async () => {
    try {
      setIsLoading(true);
      const result = await fetcherRef.current({
        offset: 0,
        limit: pageSizeRef.current,
      });
      setItems(result.data);
      itemCountRef.current = result.data.length;
      setTotalCount(result.count);
    } catch (error) {
      console.error("useLoadMore: initial fetch failed", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadMore = useCallback(async () => {
    setIsLoadingMore(true);
    try {
      const result = await fetcherRef.current({
        offset: itemCountRef.current,
        limit: pageSizeRef.current,
      });

      setItems((prev) => {
        const next = [...prev, ...result.data];
        itemCountRef.current = next.length;
        return next;
      });
      setTotalCount(result.count);
    } catch (error) {
      console.error("useLoadMore: load more failed", error);
    } finally {
      setIsLoadingMore(false);
    }
  }, []);

  const reset = useCallback(async () => {
    setItems([]);
    setTotalCount(0);
    itemCountRef.current = 0;
    await loadInitial();
  }, [loadInitial]);

  // Auto-fetch on mount and whenever resetKey changes.
  useEffect(() => {
    const resetKeyChanged = resetKeyRef.current !== resetKey;
    resetKeyRef.current = resetKey;

    if (enabled) {
      if (!resetKeyChanged && itemCountRef.current > 0) {
        return;
      }
      setItems([]);
      setTotalCount(0);
      itemCountRef.current = 0;
      loadInitial();
    }
  }, [enabled, loadInitial, resetKey]);

  const hasMore = items.length < totalCount;

  return {
    items,
    totalCount,
    isLoading,
    isLoadingMore,
    hasMore,
    loadMore,
    reset,
  };
}
